/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description constants
 */

import { CUSTOMER_ARCHETYPE } from '@veritable/common/constants';

// todo check page roles
// todo remove color hex code and gradient from user roles

const internalAdmin: UserManagement.InternalAdminRole = 'VT_INTERNAL_ADMIN';
const superAdmin: UserManagement.SuperAdminRole = 'VT_SUPER_ADMIN';
const billingAdmin: UserManagement.BillingAdminRole = 'VT_BILLING_ADMIN';
const billingUser: UserManagement.BillingUserRole = 'VT_BILLING_USER';
const analyticsUser: UserManagement.AnalyticsUserRole = 'VT_ANALYTICS_USER';

/**
 * roles by priority , used in app actions
 */
const ROLES_BY_PRIORITY: UserManagement.IRoleName[] = [internalAdmin, superAdmin, billingAdmin, billingUser, analyticsUser];

/**
 * page roles for user access  --> Control No Access from here
 */
const ELIGIBILITY_CUSTOMER_PAGE_ROLES: UserManagement.PageRoles<UserManagement.EligibilityArcheTypeRoles> = {
	eligibility: {
		responses: [internalAdmin, superAdmin, billingUser],
		newRequest: [internalAdmin, superAdmin, billingUser],
		editRequest: [internalAdmin, superAdmin, billingUser],
		viewResponse: [internalAdmin, superAdmin, billingUser],
		batch: {
			add: [internalAdmin, superAdmin, billingUser],
			edit: [internalAdmin, superAdmin, billingUser],
			viewResponse: [internalAdmin, superAdmin, billingUser],
		},
	},
	claims: {
		responses: [internalAdmin, superAdmin, billingUser],
		newRequest: [internalAdmin, superAdmin, billingUser],
		viewResponse: [internalAdmin, superAdmin, billingUser],
		editRequest: [internalAdmin, superAdmin, billingUser],
		resubmit: [internalAdmin, superAdmin, billingUser],
		reviewSecondaryClaim: [],
		filePrimaryClaim: [],
		eligibilityResponse: [],
		newEligibilityRequest: [],
		editEligibilityRequest: [],
		draftClaims: [],
		archivedClaims: [],
		worklist: {
			listView: [],
			viewResponse: [],
			edit: [],
			addManual: [],
			add: [],
		},
	},
	payments: {
		allPayments: [],
		recordPayment: [],
		paymentAllocation: [],
		era: {
			listView: [],
			viewResponse: [],
		},
	},
	accounts: {
		allAccounts: [],
		accountDetails: [],
		guarantor: [],
	},
	patients: {
		allPatients: [],
		PatientDetails: [],
	},

	analytics: {
		dashboard: [],
	},

	codeExplorer: {
		landing: [internalAdmin, superAdmin, billingUser],
		allCodes: [internalAdmin, superAdmin, billingUser],
		searchResults: [internalAdmin, superAdmin, billingUser],
		codeDetails: [internalAdmin, superAdmin, billingUser],
		favoriteCodes: [internalAdmin, superAdmin, billingUser],
		codeList: [internalAdmin, superAdmin, billingUser],
	},
	settings: {
		organization: {
			general: [internalAdmin, superAdmin, billingUser],
			users: {
				// Only Super Admin can manage users
				listView: [internalAdmin, superAdmin, billingUser],
				add: [internalAdmin, superAdmin],
				edit: [internalAdmin, superAdmin],
			},
			// billing user does not have access to these screens
			subscriptions: [internalAdmin, superAdmin],
			auditLogs: [internalAdmin, superAdmin],
		},
		dataManagement: {
			providers: {
				listView: [internalAdmin, superAdmin, billingUser],
				add: [internalAdmin, superAdmin, billingUser],
				edit: [internalAdmin, superAdmin, billingUser],
				updateStatus: [internalAdmin, superAdmin, billingUser],
			},
			locations: {
				listView: [internalAdmin, superAdmin, billingUser],
				add: [internalAdmin, superAdmin, billingUser],
				edit: [internalAdmin, superAdmin, billingUser],
				updateStatus: [internalAdmin, superAdmin, billingUser],
			},
			payers: {
				listView: [internalAdmin, superAdmin, billingUser],
				edit: [internalAdmin, superAdmin, billingUser],
				update: [internalAdmin, superAdmin, billingUser],
			},
			worklists: {
				config: [],
				edit: [],
			},
			claims: {
				config: [],
				edit: [],
				add: [],
			},
			customAttributes: {
				listView: [],
				add: [],
				edit: [],
			},
		},
		integration: {
			payment: [],
			api: [],
			clearingHouse: {
				listView: [],
				add: [],
				edit: [],
			},
		},
	},
};

const BILLING_CUSTOMER_PAGE_ROLES: UserManagement.PageRoles<UserManagement.BillingArcheTypeRoles> = {
	eligibility: {
		responses: [],
		newRequest: [],
		editRequest: [],
		viewResponse: [],
		batch: {
			add: [],
			edit: [],
			viewResponse: [],
		},
	},
	claims: {
		responses: [internalAdmin, superAdmin, billingAdmin, billingUser],
		newRequest: [],
		viewResponse: [internalAdmin, superAdmin, billingAdmin, billingUser],
		editRequest: [],
		resubmit: [internalAdmin, superAdmin, billingAdmin, billingUser],
		reviewSecondaryClaim: [internalAdmin, superAdmin, billingAdmin, billingUser],
		filePrimaryClaim: [internalAdmin, superAdmin, billingAdmin, billingUser],
		eligibilityResponse: [internalAdmin, superAdmin, billingAdmin, billingUser],
		newEligibilityRequest: [internalAdmin, superAdmin, billingAdmin, billingUser],
		editEligibilityRequest: [internalAdmin, superAdmin, billingAdmin, billingUser],
		draftClaims: [internalAdmin, superAdmin, billingAdmin, billingUser],
		archivedClaims: [internalAdmin, superAdmin, billingAdmin, billingUser],
		// worklist
		// Super Admin and Billing Admin can manage team-wide worklists
		// Billing User can only manage their own manual worklists
		worklist: {
			listView: [internalAdmin, superAdmin, billingAdmin, billingUser],
			viewResponse: [internalAdmin, superAdmin, billingAdmin, billingUser],
			edit: [internalAdmin, superAdmin, billingAdmin, billingUser],
			addManual: [internalAdmin, superAdmin, billingAdmin],
			add: [internalAdmin, superAdmin, billingAdmin],
		},
	},
	payments: {
		allPayments: [internalAdmin, superAdmin, billingAdmin, billingUser],
		recordPayment: [internalAdmin, superAdmin, billingAdmin, billingUser],
		paymentAllocation: [internalAdmin, superAdmin, billingAdmin, billingUser],
		era: {
			listView: [internalAdmin, superAdmin, billingAdmin, billingUser],
			viewResponse: [internalAdmin, superAdmin, billingAdmin, billingUser],
		},
	},
	accounts: {
		allAccounts: [internalAdmin, superAdmin, billingAdmin, billingUser],
		accountDetails: [internalAdmin, superAdmin, billingAdmin, billingUser],
		guarantor: [internalAdmin, superAdmin, billingAdmin, billingUser],
	},
	patients: {
		allPatients: [internalAdmin, superAdmin, billingAdmin, billingUser],
		PatientDetails: [internalAdmin, superAdmin, billingAdmin, billingUser],
	},

	analytics: {
		// Analytics User has read-only access
		dashboard: [internalAdmin, superAdmin, billingAdmin, billingUser, analyticsUser],
	},

	codeExplorer: {
		landing: [internalAdmin, superAdmin, billingAdmin, billingUser],
		allCodes: [internalAdmin, superAdmin, billingAdmin, billingUser],
		searchResults: [internalAdmin, superAdmin, billingAdmin, billingUser],
		codeDetails: [internalAdmin, superAdmin, billingAdmin, billingUser],
		favoriteCodes: [internalAdmin, superAdmin, billingAdmin, billingUser],
		codeList: [internalAdmin, superAdmin, billingAdmin, billingUser],
	},
	settings: {
		organization: {
			general: [internalAdmin, superAdmin, billingAdmin, billingUser],
			users: {
				// Only Super Admin can manage users
				listView: [internalAdmin, superAdmin, billingAdmin, billingUser],
				add: [internalAdmin, superAdmin],
				edit: [internalAdmin, superAdmin],
			},
			// Only Super Admin has subscription access
			subscriptions: [internalAdmin, superAdmin, billingAdmin],
			auditLogs: [internalAdmin, superAdmin, billingAdmin],
		},
		dataManagement: {
			providers: {
				listView: [internalAdmin, superAdmin, billingAdmin, billingUser],
				add: [],
				edit: [],
				updateStatus: [internalAdmin, superAdmin, billingAdmin, billingUser],
			},
			locations: {
				listView: [internalAdmin, superAdmin, billingAdmin, billingUser],
				add: [],
				edit: [],
				updateStatus: [],
			},
			payers: {
				listView: [internalAdmin, superAdmin, billingAdmin, billingUser],
				edit: [internalAdmin, superAdmin, billingUser],
				update: [internalAdmin, superAdmin, billingAdmin],
			},
			worklists: {
				config: [internalAdmin, superAdmin, billingAdmin, billingUser],
				edit: [internalAdmin, superAdmin, billingAdmin],
			},
			claims: {
				config: [internalAdmin, superAdmin, billingAdmin, billingUser],
				edit: [internalAdmin, superAdmin],
				add: [internalAdmin, superAdmin],
			},
			customAttributes: {
				listView: [internalAdmin, superAdmin, billingAdmin, billingUser],
				add: [internalAdmin, superAdmin, billingAdmin, billingUser],
				edit: [internalAdmin, superAdmin, billingAdmin, billingUser],
			},
		},
		integration: {
			payment: [internalAdmin, superAdmin],
			api: [internalAdmin, superAdmin],
			clearingHouse: {
				listView: [internalAdmin],
				add: [internalAdmin],
				edit: [internalAdmin],
			},
		},
	},
};

/**
 * user-role metadata map. Descriptions vary by customer type:
 * @returns {Record<UserManagement.IRoleName, UserManagement.RoleMeta>} role metadata keyed by role name
 */
const USER_ROLES_META: Record<UserManagement.IRoleName, UserManagement.RoleMeta> = {
	[internalAdmin]: {
		displayOrder: 5,
		title: 'Internal Admin',
		role: internalAdmin,
		color: 'blue',
		hexCode: '#096dd9',
		description: {
			[CUSTOMER_ARCHETYPE.archetype1]: 'Full platform access, including all settings and system configuration',
			[CUSTOMER_ARCHETYPE.archetype2]:
				'Full platform access, including integrations and system configuration; can manage all worklists and users',
		},
	},
	[superAdmin]: {
		displayOrder: 3,
		title: 'Super Admin',
		color: 'gold',
		hexCode: '#d48806',
		role: superAdmin,
		description: {
			[CUSTOMER_ARCHETYPE.archetype1]:
				'Access to all screens, including Subscription and Audit Log; can manage users and all settings',
			[CUSTOMER_ARCHETYPE.archetype2]:
				'Access to all screens, including Subscription and Audit Log; can manage team-wide worklists and add/deactivate users',
		},
	},
	[billingAdmin]: {
		displayOrder: 2,
		title: 'Billing Admin',
		color: 'orange',
		hexCode: '#EC9A29',
		role: billingAdmin,
		description: {
			[CUSTOMER_ARCHETYPE.archetype1]: '',
			[CUSTOMER_ARCHETYPE.archetype2]:
				'Access to Eligibility, Claims, Accounts, Payments, Patients, Analytics; can manage team-wide worklists, provider enrollment, and custom attributes',
		},
	},
	[billingUser]: {
		displayOrder: 1,
		title: 'Billing User',
		color: 'magenta',
		hexCode: '#c41d7f',
		role: billingUser,
		description: {
			[CUSTOMER_ARCHETYPE.archetype1]: 'Access to Eligibility, Claim Status, Providers, Payers, Locations; settings are view-only',
			[CUSTOMER_ARCHETYPE.archetype2]:
				'Access to Eligibility, Claims, Accounts, Payments, Patients, Analytics; can manage their own manual worklists',
		},
	},
	[analyticsUser]: {
		displayOrder: 4,
		title: 'Analytics User',
		color: 'green',
		hexCode: '#52c41a',
		role: analyticsUser,
		description: {
			[CUSTOMER_ARCHETYPE.archetype1]: '',
			[CUSTOMER_ARCHETYPE.archetype2]: 'Access to the Analytics dashboard only',
		},
	},
};

const FAQ_CATEGORIES = {
	Onboarding: {
		category: 'onboarding',
		title: 'Account Setup and Onboarding',
	},
	Provider: {
		category: 'provider',
		title: 'Provider Setup FAQs',
	},
	Payer: {
		category: 'payer',
		title: 'Payer Setup FAQs',
	},
	Eligibility: {
		category: 'eligibility',
		title: 'Eligibility Verification',
	},
	ClaimStatus: {
		category: 'claim',
		title: 'Claim Status Checks',
	},
	// TODO
	Payments: {
		category: 'payments',
		title: 'Payments',
	},
	Accounts: {
		category: 'accounts',
		title: 'Accounts',
	},
	Patients: {
		category: 'patients',
		title: 'Patients',
	},
	// TODO
	CodeExplorer: {
		category: 'code-explorer',
		title: 'Code Explorer',
	},
	// Addon: {
	// 	category: 'addon',
	// 	title: 'Payments Add-on',
	// },
	Support: {
		category: 'support',
		title: 'Support',
	},
	ClearingHouse: {
		category: 'clearing-house',
		title: 'Clearing House',
	},
};

/**
 * Page-level feature flags derived from the FeatureConfig API response.
 * Each leaf is a boolean indicating whether that page is enabled for the current tenant.
 */
const getPageFeatures = (features: App.Features, isBillingCustomer: boolean): UserManagement.PageFeatures => {
	const canAccessIndependentEligibility = !!features?.eligibility?.api?.independent;
	const canAccessPatientBasedEligibility = !!features?.eligibility?.api?.patientbased;
	const canAccessEligibilityBatchUpload = !!features?.eligibility?.batch?.upload;
	const canAccessIndependentClaimStatus = !!features?.claims?.statusapi?.independent;
	const canAccessDependentClaimStatus = !!features?.claims?.statusapi?.claimbased;
	const canAccessClaimFiling = !!features?.claims?.filing;
	const canAccessClaimsWorklists = !!features?.claims?.worklists;
	const canAccessAccounts = !!features?.accounts?.enabled;
	const canAccessAccountsGuarantors = !!features?.accounts?.guarantors;
	const canAccessPayments = !!features?.payments?.patient;
	const canAccessInsurance = !!features?.payments?.insurance;
	const canAccessPatients = !!features?.patients?.info;
	const canAccessCodeExplorer = !!features?.codeexplorer?.enabled;
	const canAccessAnalytics = !!features?.analytics?.dashboard;

	return {
		eligibility: {
			responses: canAccessIndependentEligibility,
			newRequest: canAccessIndependentEligibility,
			editRequest: canAccessIndependentEligibility,
			viewResponse: canAccessIndependentEligibility,
			batch: {
				add: canAccessEligibilityBatchUpload,
				edit: canAccessEligibilityBatchUpload,
				viewResponse: canAccessEligibilityBatchUpload,
			},
		},
		claims: {
			responses: true,
			viewResponse: true,
			newRequest: canAccessIndependentClaimStatus,
			editRequest: canAccessIndependentClaimStatus,
			resubmit: canAccessClaimFiling,
			reviewSecondaryClaim: canAccessClaimFiling,
			filePrimaryClaim: canAccessClaimFiling,
			eligibilityResponse: canAccessPatientBasedEligibility,
			newEligibilityRequest: canAccessPatientBasedEligibility,
			editEligibilityRequest: canAccessPatientBasedEligibility,
			draftClaims: canAccessDependentClaimStatus,
			archivedClaims: canAccessDependentClaimStatus,
			worklist: {
				listView: canAccessClaimsWorklists,
				add: canAccessClaimsWorklists,
				addManual: canAccessClaimsWorklists,
				edit: canAccessClaimsWorklists,
				viewResponse: canAccessClaimsWorklists,
			},
		},
		payments: {
			allPayments: canAccessPayments,
			recordPayment: canAccessPayments,
			paymentAllocation: canAccessPayments,
			era: {
				listView: canAccessInsurance,
				viewResponse: canAccessInsurance,
			},
		},
		accounts: {
			allAccounts: canAccessAccounts,
			accountDetails: canAccessAccounts,
			guarantor: canAccessAccountsGuarantors,
		},
		patients: {
			allPatients: canAccessPatients,
			PatientDetails: canAccessPatients,
		},
		analytics: {
			dashboard: canAccessAnalytics,
		},
		codeExplorer: {
			landing: canAccessCodeExplorer,
			allCodes: canAccessCodeExplorer,
			searchResults: canAccessCodeExplorer,
			codeDetails: canAccessCodeExplorer,
			favoriteCodes: canAccessCodeExplorer,
			codeList: canAccessCodeExplorer,
		},
		settings: {
			organization: {
				general: true,
				users: { listView: true, add: true, edit: true },
				subscriptions: true,
				auditLogs: true,
			},
			dataManagement: {
				providers: {
					listView: true,
					add: !isBillingCustomer,
					edit: !isBillingCustomer,
					updateStatus: true,
				},
				locations: { listView: true, add: true, edit: true, updateStatus: true },
				payers: { listView: true, edit: true, update: true },
				worklists: { config: canAccessClaimsWorklists, edit: canAccessClaimsWorklists },
				claims: { config: canAccessDependentClaimStatus, edit: canAccessDependentClaimStatus, add: canAccessDependentClaimStatus },
				customAttributes: {
					listView: isBillingCustomer,
					add: isBillingCustomer,
					edit: isBillingCustomer,
				},
			},
			integration: {
				payment: true,
				api: true,
				clearingHouse: { listView: isBillingCustomer, add: isBillingCustomer, edit: isBillingCustomer },
			},
		},
	};
};

export {
	getPageFeatures,
	internalAdmin as INTERNAL_ADMIN_ROLE,
	superAdmin as SUPER_ADMIN_ROLE,
	billingAdmin as BILLING_ADMIN_ROLE,
	billingUser as BILLING_USER_ROLE,
	analyticsUser as ANALYTICS_USER_ROLE,
	USER_ROLES_META,
	ROLES_BY_PRIORITY,
	ELIGIBILITY_CUSTOMER_PAGE_ROLES,
	BILLING_CUSTOMER_PAGE_ROLES,
	FAQ_CATEGORIES,
};
